// frontend/src/utils/relationships/processRelationshipNetworkMemory.js

import { debugLog } from '../logging/debugLog';
import {
  buildResolvedRelationshipAnchors,
  extractRelationshipNetworkCandidates,
} from './extractRelationshipNetworkCandidates';
import { validateRelationshipNetworkCandidates } from './validateRelationshipNetworkCandidate';
import { upsertRelationshipNetworkEdge } from '../memory/mongoMemoryUtils';
import {
  buildRelationshipEdgeIdentityKey,
  buildRelationshipEdgeKey,
} from './relationshipOntology';

function getActiveRelationshipEdges(relationshipNetwork = null) {
  const edges = Array.isArray(relationshipNetwork)
    ? relationshipNetwork
    : Array.isArray(relationshipNetwork?.edges)
      ? relationshipNetwork.edges
      : [];

  return edges.filter((edge) => edge?.isActive !== false);
}

function isReadOnlyRelationshipTurn({ message = '', conversationState = null } = {}) {
  const turnOperations =
    conversationState?.activeFrameState?.situation?.turnOperations?.operations || [];
  const relationshipReferenceState =
    conversationState?.relationshipReferenceState ||
    conversationState?.turnInterpretation?.relationshipReferenceState ||
    null;
  const turnFunctionType = String(
    conversationState?.intentState?.turnFunction?.type || ''
  ).trim().toUpperCase();

  if (turnFunctionType === 'QUESTION') {
    return true;
  }

  if (
    Array.isArray(turnOperations) &&
    turnOperations.some((operation) => operation?.type === 'relationship_recall_request')
  ) {
    return true;
  }

  if (relationshipReferenceState?.recallLike === true) {
    return true;
  }

  return String(message || '').trim().endsWith('?');
}

function isRelationshipBearingTurn(conversationState = null) {
  const relationshipReferenceState =
    conversationState?.relationshipReferenceState ||
    conversationState?.turnInterpretation?.relationshipReferenceState ||
    null;

  const mentionCount = Number(
    relationshipReferenceState?.evidence?.relationshipMentionCount || 0
  );

  if (mentionCount > 0) return true;

  return Array.isArray(relationshipReferenceState?.roleResolutions) &&
    relationshipReferenceState.roleResolutions.length > 0;
}

function getRootEntityIds(edges = []) {
  const ids = new Set();

  edges.forEach((edge) => {
    if (edge?.fromIsRoot === true && edge?.fromEntityId) ids.add(edge.fromEntityId);
    if (edge?.toIsRoot === true && edge?.toEntityId) ids.add(edge.toEntityId);
  });

  return [...ids];
}

function findExistingEdge(candidate = {}, activeEdges = []) {
  const candidateIdentityKey = buildRelationshipEdgeIdentityKey(candidate);
  const candidateCompatibilityKey = buildRelationshipEdgeKey(candidate);

  if (candidateIdentityKey) {
    const identityMatch = activeEdges.find(
      (edge) => buildRelationshipEdgeIdentityKey(edge) === candidateIdentityKey
    );
    if (identityMatch) return { edge: identityMatch, matchType: 'identity' };
  }

  if (!candidateCompatibilityKey) return { edge: null, matchType: null };

  const compatibilityMatches = activeEdges.filter(
    (edge) => buildRelationshipEdgeKey(edge) === candidateCompatibilityKey
  );

  if (compatibilityMatches.length === 1) {
    return { edge: compatibilityMatches[0], matchType: 'compatibility' };
  }

  return {
    edge: null,
    matchType: compatibilityMatches.length > 1 ? 'ambiguous_compatibility' : null,
  };
}

function bindCandidateToExistingEdge(candidate = {}, existingEdge = null) {
  if (!existingEdge) return candidate;

  return {
    ...candidate,
    fromEntityId: candidate.fromEntityId || existingEdge.fromEntityId || null,
    toEntityId: candidate.toEntityId || existingEdge.toEntityId || null,
  };
}

async function writeCandidate({ candidate, validation, message, resolvedAnchors }) {
  return upsertRelationshipNetworkEdge({
    fromName: candidate.fromName,
    fromType: candidate.fromType || 'unknown',
    fromEntityId: candidate.fromEntityId || null,
    toName: candidate.toName,
    toType: candidate.toType || 'unknown',
    toEntityId: candidate.toEntityId || null,
    relation: candidate.relation,
    status: validation.status || candidate.status || 'observed',
    confidence: validation.confidence || candidate.confidence || 0,
    resolutionConfidence:
      validation.resolutionConfidence || candidate.resolutionConfidence || 0,
    relationStability: validation.relationStability || 'unknown',
    authority: validation.authority || 'explicit_user_relationship_assertion',
    source: candidate.source || 'relationship_network_candidate',
    sourceText: candidate.sourceText || message,
    evidenceCount: 1,
    isActive: true,
    metadata: {
      ...(candidate.metadata || {}),
      anchorEntityIds: resolvedAnchors.map((anchor) => anchor.entityId),
      identityAnchored: true,
    },
  });
}

export async function processRelationshipNetworkMemory({
  message,
  relationships = [],
  relationshipNetwork = null,
  conversationState = null,
  intentInfo = null,
} = {}) {
  const trimmedMessage = String(message || '').trim();

  if (!trimmedMessage) {
    return { attempted: false, saved: [], existing: [], rejected: [], reason: 'Missing message.' };
  }

  if (isReadOnlyRelationshipTurn({ message: trimmedMessage, conversationState })) {
    return {
      attempted: false,
      saved: [],
      existing: [],
      rejected: [],
      reason: 'Relationship read/question turn is non-mutating.',
    };
  }

  // Do not call the relationship extractor on ordinary conversation merely
  // because a known person is active. Relationship-role evidence is already
  // computed by Daisy's interpretation layer and is the cheap admission signal.
  if (!isRelationshipBearingTurn(conversationState)) {
    return {
      attempted: false,
      saved: [],
      existing: [],
      rejected: [],
      reason: 'No relationship-bearing turn signal.',
    };
  }

  const resolvedAnchors = buildResolvedRelationshipAnchors({
    conversationState,
    relationships,
    relationshipNetwork,
  });

  if (!resolvedAnchors.length) {
    return {
      attempted: false,
      saved: [],
      existing: [],
      rejected: [],
      reason: 'No resolved relationship entity anchor available.',
    };
  }

  const candidates = await extractRelationshipNetworkCandidates({
    message: trimmedMessage,
    relationships,
    relationshipNetwork,
    conversationState,
    resolvedAnchors,
  });

  if (!Array.isArray(candidates) || candidates.length === 0) {
    return {
      attempted: true,
      saved: [],
      existing: [],
      rejected: [],
      reason: 'No relationship network candidates extracted.',
    };
  }

  const activeEdges = getActiveRelationshipEdges(relationshipNetwork);
  const validatedCandidates = validateRelationshipNetworkCandidates({
    candidates,
    resolvedAnchors,
    rootEntityIds: getRootEntityIds(activeEdges),
    userName:
      conversationState?.identity?.userName ||
      conversationState?.userName ||
      '',
    message: trimmedMessage,
  });

  const saved = [];
  const existing = [];
  const rejected = [];
  const workingEdges = [...activeEdges];

  for (const item of validatedCandidates) {
    let candidate = item?.candidate;
    const validation = item?.validation;

    if (!candidate || !validation?.allowed) {
      rejected.push({ candidate, validation });
      continue;
    }

    const match = findExistingEdge(candidate, workingEdges);

    if (match.matchType === 'ambiguous_compatibility') {
      rejected.push({
        candidate,
        validation: {
          allowed: false,
          reason: 'Multiple same-name logical edges exist; immutable identity is required before mutation.',
        },
      });
      continue;
    }

    if (match.edge) {
      candidate = bindCandidateToExistingEdge(candidate, match.edge);
    }

    const edge = await writeCandidate({
      candidate,
      validation,
      message: trimmedMessage,
      resolvedAnchors,
    });

    if (!edge) {
      rejected.push({
        candidate,
        validation: { allowed: false, reason: 'Upsert failed.' },
      });
      continue;
    }

    saved.push(edge);

    if (match.edge) {
      existing.push({
        candidate,
        edgeKey: buildRelationshipEdgeIdentityKey(edge),
        reason: 'Existing logical edge reinforced; recency/evidence updated.',
      });
    }

    const matchIndex = workingEdges.findIndex(
      (itemEdge) =>
        buildRelationshipEdgeIdentityKey(itemEdge) ===
        buildRelationshipEdgeIdentityKey(edge)
    );

    if (matchIndex >= 0) workingEdges[matchIndex] = edge;
    else workingEdges.push(edge);
  }

  const result = {
    attempted: true,
    saved,
    existing,
    rejected,
    reason: 'Relationship network memory processed.',
  };

  if (rejected.length) {
    debugLog(
      '🕸️ [RelationshipNetwork] Rejected candidate reasons:',
      rejected.map((item) => ({
        fromName: item?.candidate?.fromName || null,
        relation: item?.candidate?.relation || null,
        toName: item?.candidate?.toName || null,
        fromEntityId: item?.candidate?.fromEntityId || null,
        toEntityId: item?.candidate?.toEntityId || null,
        confidence: item?.candidate?.confidence || 0,
        reason: item?.validation?.reason || 'Unknown rejection reason.',
      }))
    );
  }

  debugLog('🕸️ [RelationshipNetwork] Process result:', result);
  return result;
}
